import React, { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Badge, Box, Card, CardActionArea, Grid, Typography } from "@mui/material";

import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import InboxIcon from "@mui/icons-material/Inbox";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RuleFolderIcon from "@mui/icons-material/RuleFolder";
import GroupsIcon from "@mui/icons-material/Groups";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import RuleIcon from "@mui/icons-material/Rule";

import { PERMISSIONS } from "../../constants";
import {
  subscribePendingApprovals,
  subscribeExpiredInfoRequests,
  subscribePendingAppeals,
  subscribeTriageCount,
} from "../../services/backend/PqrsdService";
import { listPqrsdGroups } from "../../services/backend/PqrsdConfigService";
import { selectInbox, selectMine, selectExpired, selectAppeals } from "./approvalQueueSelectors";

import { pqrsdText } from "./pqrsdLabel";
// Unified PQRSD entry point. A user may hold any combination of PQRSD
// capabilities (supervisor list, operator queue, ventanilla intake). This hub
// shows a tile per capability the user actually has. When only one capability
// is available we skip the selector and redirect straight to it.
export default function PqrsdHub() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  // Live count for the "Casos a revisar" badge: what makes the tile an inbox
  // rather than another link. Both sides of the wait are counted — items this
  // user should review, and items they submitted and are still waiting on.
  const [pending, setPending] = useState([]);
  const [expired, setExpired] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [supervisedGroupIds, setSupervisedGroupIds] = useState(null);
  const myId = user?.userId || user?.userUID;
  const perms = useMemo(() => user?.permissions || [], [user]);
  const canApprove = perms.includes(PERMISSIONS.PQRSD.APPROVE_RESPONSE);
  const canDecree = perms.includes(PERMISSIONS.PQRSD.DECREE_DESISTIMIENTO);
  const canSeeAll = perms.includes(PERMISSIONS.PQRSD.LIST);
  // "Casos a revisar" is for the people on either side of an approval: who
  // drafts a requerimiento, who approves it, who decrees on an expired one.
  // Scoped roles (ventanilla, config, informes) have nothing to do there.
  const hasReviewQueue = [
    PERMISSIONS.PQRSD.REQUEST_INFO,
    PERMISSIONS.PQRSD.APPROVE_RESPONSE,
    PERMISSIONS.PQRSD.DECREE_DESISTIMIENTO,
  ].some((p) => perms.includes(p));
  const [triageCount, setTriageCount] = useState(0);

  useEffect(() => {
    if (!hasReviewQueue) return undefined;
    return subscribePendingApprovals(setPending);
  }, [hasReviewQueue]);

  // A PQRSD the AI could not route is assigned to nobody and sits in no
  // dependency, so no other queue in the app would ever show it. The badge is
  // the only thing standing between it and a term that runs out unnoticed.
  useEffect(() => {
    if (!canSeeAll) return undefined;
    return subscribeTriageCount(setTriageCount);
  }, [canSeeAll]);

  // Expired requerimientos count on the same badge, for the same reason the
  // approvals do: a suspended PQRSD waiting on a decision is not something to
  // find out about by opening it.
  useEffect(() => {
    if (!canDecree) return undefined;
    return subscribeExpiredInfoRequests(setExpired);
  }, [canDecree]);
  // And recursos waiting to be resolved: two months, then silence is a denial.
  useEffect(() => {
    if (!canDecree) return undefined;
    return subscribePendingAppeals(setAppeals);
  }, [canDecree]);

  // The dependencies this user leads scope both the requerimientos they may
  // approve and the expired ones they may decree on.
  useEffect(() => {
    if (!hasReviewQueue || !myId) return;
    let alive = true;
    (async () => {
      const groups = await listPqrsdGroups();
      if (!alive) return;
      setSupervisedGroupIds(
        groups
          .filter((g) => Array.isArray(g.supervisorIds) && g.supervisorIds.includes(myId))
          .map((g) => g.id)
      );
    })();
    return () => { alive = false; };
  }, [hasReviewQueue, myId]);

  const pendingCount = useMemo(
    () =>
      selectInbox(pending, { myId, canApprove, supervisedGroupIds }).length +
      selectMine(pending, { myId }).length +
      selectExpired(expired, { canSeeAll, supervisedGroupIds }).length +
      (canDecree ? selectAppeals(appeals, { canSeeAll, supervisedGroupIds, myId }).length : 0),
    [pending, expired, appeals, myId, canApprove, canDecree, canSeeAll, supervisedGroupIds]
  );

  const options = useMemo(() => {
    const perms = user?.permissions || [];
    const items = [];
    const iconSx = { fontSize: { xs: 44, md: 56 } };
    if (perms.includes(PERMISSIONS.PQRSD.LIST)) {
      items.push({
        key: "admin",
        label: pqrsdText("Gestionar PQRSDs"),
        description: "Revisa, asigna y responde las solicitudes ciudadanas.",
        icon: <SupportAgentIcon sx={iconSx} />,
        path: "/pqrsd/admin",
      });
    }
    if (perms.includes(PERMISSIONS.PQRSD.MINE)) {
      items.push({
        key: "mine",
        label: pqrsdText("Mis PQRSDs"),
        description: pqrsdText("Atiende las PQRSD que tienes asignadas."),
        icon: <InboxIcon sx={iconSx} />,
        path: "/pqrsd/mine",
      });
      items.push({
        key: "group",
        label: "Mi dependencia",
        description: pqrsdText("PQRSD que la IA enrutó a tu dependencia."),
        icon: <GroupsIcon sx={iconSx} />,
        path: "/pqrsd/dependencia",
      });
    }
    if (perms.includes(PERMISSIONS.PQRSD.LIST)) {
      items.push({
        key: "triage",
        label: "Triage IA",
        description: pqrsdText("Revisa y clasifica las PQRSD que la IA no pudo enrutar."),
        icon: <RuleFolderIcon sx={iconSx} />,
        path: "/pqrsd/triage",
        badgeCount: triageCount,
      });
    }
    if (perms.includes(PERMISSIONS.PQRSD.WINDOW_INTAKE)) {
      items.push({
        key: "window",
        label: "Radicar por ventanilla",
        description: "Registra una nueva solicitud presencial.",
        icon: <StorefrontIcon sx={iconSx} />,
        path: "/pqrsd/ventanilla",
      });
    }
    if (perms.includes(PERMISSIONS.PQRSD.CONFIG)) {
      items.push({
        key: "config",
        label: pqrsdText("Config PQRSD"),
        description: "Clasificador de IA, correos y dependencias de enrutamiento.",
        icon: <SmartToyIcon sx={iconSx} />,
        path: "/pqrsd/config",
      });
    }
    // Whichever side of the approval they are on: the reviewer has something
    // to act on, the drafter is waiting on someone.
    if (hasReviewQueue) {
      items.push({
        key: "approvals",
        label: "Casos a revisar",
        description: canDecree
          ? "Requerimientos por aprobar, requerimientos vencidos y recursos de reposición por resolver."
          : "Requerimientos redactados que esperan una aprobación.",
        icon: <RuleIcon sx={iconSx} />,
        path: "/pqrsd/approvals",
        badgeCount: pendingCount,
      });
    }
    if (perms.includes(PERMISSIONS.PQRSD.REPORTS)) {
      items.push({
        key: "reports",
        label: "Informes",
        description: "Estadísticas de atención y clasificación por IA.",
        icon: <QueryStatsIcon sx={iconSx} />,
        path: "/pqrsd/informes",
      });
    }
    return items;
  }, [user, canDecree, hasReviewQueue, pendingCount, triageCount]);

  // Single capability -> go straight there, no selector needed.
  useEffect(() => {
    if (options.length === 1) {
      navigate(options[0].path, { replace: true });
    }
  }, [options, navigate]);

  if (options.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          {pqrsdText("Sin acceso a PQRSD")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {pqrsdText("Tu usuario no tiene permisos para ningún módulo de PQRSD.")}
        </Typography>
      </Box>
    );
  }

  // While the single-capability redirect runs, render nothing to avoid a flash.
  if (options.length === 1) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "70vh",
        px: { xs: 2, md: 4 },
        py: 4,
      }}
    >
      <Typography variant="h4" gutterBottom>
        {pqrsdText("PQRSD")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Selecciona un módulo
      </Typography>

      <Grid
        container
        spacing={3}
        justifyContent="center"
        sx={{ width: "100%", maxWidth: 900 }}
      >
        {options.map((opt) => (
          <Grid item xs={12} sm={6} md={4} key={opt.key}>
            <Card
              sx={{
                height: "100%",
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  boxShadow: 6,
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(opt.path)}
                sx={{ height: "100%" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 3,
                  }}
                >
                  <Box sx={{ color: "primary.main", mb: 1.5 }}>
                    {opt.badgeCount ? (
                      <Badge
                        badgeContent={opt.badgeCount}
                        max={99}
                        color="error"
                        overlap="circular"
                      >
                        {opt.icon}
                      </Badge>
                    ) : (
                      opt.icon
                    )}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {opt.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {opt.description}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
